import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ExperienceOption {
  value: string;
  label: string;
}

interface ExperienceModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedValues: {
    totalExperience: string | string[];
    positionExperience: string | string[];
    experiencePeriod: string;
    excludeJumpers?: boolean;
  };
  onSelectionChange: (values: {
    totalExperience: string | string[];
    positionExperience: string | string[];
    experiencePeriod: string;
    excludeJumpers?: boolean;
  }) => void;
  onlyTotalExperience?: boolean;
}

const ExperienceModal: React.FC<ExperienceModalProps> = ({
  isOpen,
  onClose,
  selectedValues,
  onSelectionChange,
  onlyTotalExperience = false
}) => {
  const [localValues, setLocalValues] = useState<{
    totalExperience: string[];
    positionExperience: string[];
    experiencePeriod: string;
    excludeJumpers: boolean;
  }>({
    totalExperience: Array.isArray(selectedValues.totalExperience)
      ? selectedValues.totalExperience
      : selectedValues.totalExperience
        ? selectedValues.totalExperience.split(',').map(s => s.trim()).filter(Boolean)
        : [],
    positionExperience: Array.isArray(selectedValues.positionExperience)
      ? selectedValues.positionExperience
      : selectedValues.positionExperience
        ? selectedValues.positionExperience.split(',').map(s => s.trim()).filter(Boolean)
        : [],
    experiencePeriod: selectedValues.experiencePeriod || '',
    excludeJumpers: selectedValues.excludeJumpers || false
  });

  useEffect(() => {
    if (isOpen) {
      setLocalValues({
        totalExperience: Array.isArray(selectedValues.totalExperience)
          ? selectedValues.totalExperience
          : selectedValues.totalExperience
            ? selectedValues.totalExperience.split(',').map((s: string) => s.trim()).filter(Boolean)
            : [],
        positionExperience: Array.isArray(selectedValues.positionExperience)
          ? selectedValues.positionExperience
          : selectedValues.positionExperience
            ? selectedValues.positionExperience.split(',').map((s: string) => s.trim()).filter(Boolean)
            : [],
        experiencePeriod: selectedValues.experiencePeriod || '',
        excludeJumpers: selectedValues.excludeJumpers || false
      });
    }
  }, [isOpen, selectedValues.totalExperience, selectedValues.positionExperience, selectedValues.experiencePeriod, selectedValues.excludeJumpers]);

  const experienceOptions: Record<string, ExperienceOption[]> = {
    totalExperience: [
      { value: 'noExperience', label: 'Без опыта' },
      { value: 'between1And3', label: '1-3 года' },
      { value: 'between3And6', label: '3-6 лет' },
      { value: 'moreThan6', label: 'Более 6 лет' }
    ],
    positionExperience: [
      { value: 'noExperience', label: 'Без опыта' },
      { value: 'between1And3', label: '1-3 года' },
      { value: 'between3And6', label: '3-6 лет' },
      { value: 'moreThan6', label: 'Более 6 лет' }
    ],
    experiencePeriod: [
      { value: '', label: 'Любой' },
      { value: 'all_time', label: 'За все время' },
      { value: 'last_year', label: 'За последний год' },
      { value: 'last_three_years', label: 'За последние 3 года' },
      { value: 'last_six_years', label: 'За последние 6 лет' }
    ]
  };

  const handleToggleExperience = (type: 'totalExperience' | 'positionExperience', value: string) => {
    setLocalValues(prev => {
      const currentArray = prev[type];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(v => v !== value)
        : [...currentArray, value];
      return {
        ...prev,
        [type]: newArray
      };
    });
  };

  const handleSelectPeriod = (value: string) => {
    setLocalValues(prev => ({
      ...prev,
      experiencePeriod: value
    }));
  };

  const handleReset = () => {
    setLocalValues({
      totalExperience: [],
      positionExperience: [],
      experiencePeriod: '',
      excludeJumpers: false
    });
  };

  const handleSave = () => {
    // TASKNEIROKLYUCH-812: фильтр джамперов имеет смысл только при выбранном
    // опыте — иначе принудительно false, чтобы устаревший true не утёк наружу
    // (в state и далее в createApiQuery → HH backend).
    const hasExperienceSelected =
      localValues.totalExperience.length > 0 ||
      localValues.positionExperience.length > 0;
    onSelectionChange({
      totalExperience: localValues.totalExperience,
      positionExperience: localValues.positionExperience,
      experiencePeriod: localValues.experiencePeriod,
      excludeJumpers: hasExperienceSelected ? localValues.excludeJumpers : false
    });
    onClose();
  };

  const handleCancel = () => {
    setLocalValues({
      totalExperience: Array.isArray(selectedValues.totalExperience)
        ? selectedValues.totalExperience
        : selectedValues.totalExperience
          ? selectedValues.totalExperience.split(',').map((s: string) => s.trim()).filter(Boolean)
          : [],
      positionExperience: Array.isArray(selectedValues.positionExperience)
        ? selectedValues.positionExperience
        : selectedValues.positionExperience
          ? selectedValues.positionExperience.split(',').map((s: string) => s.trim()).filter(Boolean)
          : [],
      experiencePeriod: selectedValues.experiencePeriod || '',
      excludeJumpers: selectedValues.excludeJumpers || false
    });
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" data-testid="modal-overlay">
      <div className="modal-container flex flex-col" data-testid="modal-container" data-modal="experience">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-2">
          <h2 className="text-2xl font-bold text-gray-900">Опыт работы</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0">
          <div className="space-y-8">
            {/* Общий опыт */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Общий опыт работы</h3>
              <div className="flex flex-wrap gap-2">
                {experienceOptions.totalExperience.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleToggleExperience('totalExperience', option.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      localValues.totalExperience.includes(option.value)
                        ? 'text-[#e16349]'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    style={localValues.totalExperience.includes(option.value) ? { backgroundColor: 'rgba(225, 99, 73, 0.1)' } : undefined}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {!onlyTotalExperience && (
              <>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Опыт работы в должности</h3>
                  <div className="flex flex-wrap gap-2">
                    {experienceOptions.positionExperience.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleToggleExperience('positionExperience', option.value)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                          localValues.positionExperience.includes(option.value)
                            ? 'text-[#e16349]'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        style={localValues.positionExperience.includes(option.value) ? { backgroundColor: 'rgba(225, 99, 73, 0.1)' } : undefined}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Фильтр джамперов. TASKNEIROKLYUCH-812: активен ТОЛЬКО когда
                выбран опыт (totalExperience/positionExperience). Без выбранного
                опыта чекбокс задизейблен. */}
            {(() => {
              const hasExperienceSelected =
                localValues.totalExperience.length > 0 ||
                localValues.positionExperience.length > 0;
              const jumpersDisabled = !hasExperienceSelected;
              return (
                <div>
                  <label className={`flex items-center gap-3 py-2 ${jumpersDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={jumpersDisabled ? false : localValues.excludeJumpers}
                      disabled={jumpersDisabled}
                      onChange={(e) => setLocalValues(prev => ({ ...prev, excludeJumpers: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-[#e16349] focus:ring-[#e16349]"
                    />
                    <span className="text-sm font-medium text-gray-900">Исключить кандидатов-джамперов</span>
                  </label>
                  <p className="text-xs text-gray-500 ml-7">Кандидаты, у которых последние 3 места работы менее 12 месяцев</p>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 mt-auto">
          <button
            onClick={handleReset}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            Сбросить
          </button>
          <div className="flex space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Отменить
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-[#e16349] text-white hover:bg-[#d14d3a] rounded-lg transition-colors"
            >
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExperienceModal;
